import React, { useEffect, useState } from "react";
import { useParams } from "react-router";
import { useFormik } from "formik";
import Slider from "rc-slider";
import { isEmpty } from "lodash";
import { Link } from "react-router-dom";
import { restaurantAccountingSettingsSchema } from "../../../../validations";
import {
	useGetRestaurantSettings,
	useUpdateRestaurantSettings,
} from "../../../../queryHooks";
import { useStore } from "../../../../zustand/store";
import CS from "../../../../services/commonService";
import SingleStatusToggle from "../../../common/singleStatusToggle";

const AccountingSettings = () => {
	const setLoading = useStore((state) => state.setLoading);
	const { slug } = useParams();

	const [restaurantConfig, setRestaurantConfig] = useState();
	const [isDataChanged, setIsDataChanged] = useState(false);

	const { data: restaurantsSettingsData, refetch: refetchSettings } =
		useGetRestaurantSettings(slug);

	useEffect(() => {
		if (restaurantsSettingsData?.status === "success") {
			setRestaurantConfig(restaurantsSettingsData?.data);
		}
	}, [restaurantsSettingsData]);

	const {
		mutateAsync: updateRestaurantSettingsAsync,
		error: errorUpdateRestaurantSettings,
		data: dataUpdateRestaurantSettings,
		isLoading: updateRestaurantSettingsLoading,
	} = useUpdateRestaurantSettings();

	useEffect(() => {
		if (dataUpdateRestaurantSettings) {
			if (dataUpdateRestaurantSettings?.status === "success") {
				CS.showSuccessMessage(dataUpdateRestaurantSettings?.message);
				refetchSettings();
			} else if (dataUpdateRestaurantSettings?.status === "fail") {
				CS.showErrorMessage(dataUpdateRestaurantSettings?.message);
			}
		}
	}, [dataUpdateRestaurantSettings]);

	useEffect(() => {
		if (errorUpdateRestaurantSettings) {
			CS.showErrorMessage(
				errorUpdateRestaurantSettings?.response?.data?.message
			);
		}
	}, [errorUpdateRestaurantSettings]);

	const {
		values: valuesAccounting,
		errors: errorsAccounting,
		handleChange: handleChangeAccounting,
		handleSubmit: handleSubmitAccounting,
		touched: touchedAccounting,
	} = useFormik({
		enableReinitialize: true,
		initialValues: {
			tipsStatus: restaurantConfig?.tips_enabled
				? !!restaurantConfig?.tips_enabled
				: false,
			serviceChargeStatus: restaurantConfig?.service_charge_enabled
				? !!restaurantConfig?.service_charge_enabled
				: false,
			isServiceChargeTaxable: restaurantConfig?.service_charge_taxable
				? !!restaurantConfig?.service_charge_taxable
				: false,
			serviceCharge: isNaN(restaurantConfig?.service_charge_percentage)
				? ""
				: restaurantConfig?.service_charge_percentage,
		},
		validationSchema: restaurantAccountingSettingsSchema,
		onSubmit: (value) => {
			let body = {
				...(!!restaurantConfig?.service_charge_enabled !==
					value.serviceChargeStatus && {
					service_charge_enabled: value.serviceChargeStatus,
				}),
				...(!!restaurantConfig?.tips_enabled !== value.tipsStatus && {
					tips_enabled: value.tipsStatus,
				}),
				...(restaurantConfig?.service_charge_percentage !==
					valuesAccounting.serviceCharge &&
					value.serviceChargeStatus && {
						service_charge_percentage: value.serviceCharge,
					}),
				...(!!restaurantConfig?.service_charge_taxable !==
					value.isServiceChargeTaxable &&
					value.serviceChargeStatus && {
						service_charge_taxable: value.isServiceChargeTaxable,
					}),
			};
			if (!isEmpty(body))
				updateRestaurantSettingsAsync({
					restaurantId: slug,
					body,
				});
		},
	});

	useEffect(() => {
		let body = {
			...(!!restaurantConfig?.service_charge_enabled !==
				valuesAccounting.serviceChargeStatus && {
				service_charge_enabled: valuesAccounting.serviceChargeStatus,
			}),
			...(!!restaurantConfig?.tips_enabled !== valuesAccounting.tipsStatus && {
				tips_enabled: valuesAccounting.tipsStatus,
			}),
			...(restaurantConfig?.service_charge_percentage !==
				valuesAccounting.serviceCharge &&
				valuesAccounting.serviceChargeStatus && {
					service_charge_percentage: valuesAccounting.serviceCharge,
				}),
			...(!!restaurantConfig?.service_charge_taxable !==
				valuesAccounting.isServiceChargeTaxable &&
				valuesAccounting.serviceChargeStatus && {
					service_charge_taxable: valuesAccounting.isServiceChargeTaxable,
				}),
		};

		if (Object.keys(body).length > 0) {
			setIsDataChanged(true);
		} else {
			setIsDataChanged(false);
		}
	}, [valuesAccounting, restaurantConfig]);

	useEffect(() => {
		if (!valuesAccounting.serviceChargeStatus) {
			let eventTax = {
				target: {
					name: "isServiceChargeTaxable",
					value: !!restaurantConfig?.service_charge_taxable,
				},
			};
			handleChangeAccounting(eventTax);
			let eventCharge = {
				target: {
					name: "serviceCharge",
					value: restaurantConfig?.service_charge_percentage,
				},
			};
			handleChangeAccounting(eventCharge);
		}
	}, [valuesAccounting.serviceChargeStatus]);

	useEffect(() => {
		setLoading(updateRestaurantSettingsLoading);
	}, [updateRestaurantSettingsLoading]);

	return (
		<>
			<form onSubmit={handleSubmitAccounting}>
				<div className="card restaurant-card mb-5">
					<div className="modal-header p-0 pb-3 mb-4 w-100">
						<h5 className="box-title modal-title">
							<i className="bx bx-user mr-1"></i> Accounting Settings
						</h5>
					</div>
					<div className="row align-items-center">
						<div className="col-md-12 mt-3">
							<div className="row">
								<div className="form-group col-md-6 mb-md-4 mb-3">
									<div className="d-flex justify-content-between">
										<label className="mb-0">Enable Service Charge</label>
										<SingleStatusToggle
											_id={"serviceChargeStatus"}
											status={valuesAccounting.serviceChargeStatus}
											onToggleChange={() => {
												let event = {
													target: {
														name: "serviceChargeStatus",
														value: !valuesAccounting.serviceChargeStatus,
													},
												};
												handleChangeAccounting(event);
											}}
											htmlFor={"serviceChargeStatus"}
										/>
									</div>
									{valuesAccounting.serviceChargeStatus ? (
										<>
											<div className="row">
												<div className="form-group col-md-12 mt-3">
													<label className="mb-0">Service Charge(%)</label>
													<input
														className="form-control mt-2"
														id="serviceCharge"
														name="serviceCharge"
														placeholder="Enter service charge"
														onKeyUp={(e) => {
															e.target.value = Math.abs(e.target.value);
															handleChangeAccounting(e);
														}}
														value={valuesAccounting.serviceCharge}
														type="number"
														onChange={handleChangeAccounting}
													/>
													<Slider
														className="mt-3"
														min={0}
														max={100}
														value={valuesAccounting.serviceCharge}
														onChange={(e) => {
															handleChangeAccounting({
																target: { name: "serviceCharge", value: e },
															});
														}}
													/>
													<span className="text-danger d-block">
														{errorsAccounting.serviceCharge &&
														touchedAccounting.serviceCharge
															? errorsAccounting.serviceCharge
															: null}
													</span>
												</div>
												<div className="col-md-12">
													<div className="form-group mb-0">
														<div className="d-flex justify-content-between mb-3 mt-3">
															<label className="mb-0">
																Service Charge Taxable
															</label>
															<SingleStatusToggle
																_id={"isServiceChargeTaxable"}
																status={valuesAccounting.isServiceChargeTaxable}
																onToggleChange={() => {
																	let event = {
																		target: {
																			name: "isServiceChargeTaxable",
																			value:
																				!valuesAccounting.isServiceChargeTaxable,
																		},
																	};
																	handleChangeAccounting(event);
																}}
																htmlFor={"isServiceChargeTaxable"}
															/>
														</div>
													</div>
												</div>
											</div>
										</>
									) : null}
								</div>
								<div className="form-group col-md-6 mb-md-4 mb-3">
									<div className="d-flex justify-content-between">
										<label className="mb-0">Enable Tips</label>
										<SingleStatusToggle
											_id={"tipsStatus"}
											status={valuesAccounting.tipsStatus}
											onToggleChange={() => {
												let event = {
													target: {
														name: "tipsStatus",
														value: !valuesAccounting.tipsStatus,
													},
												};
												handleChangeAccounting(event);
											}}
											htmlFor={"tipsStatus"}
										/>
									</div>

									<span className="text-danger d-block">
										{errorsAccounting.tipsStatus && touchedAccounting.tipsStatus
											? errorsAccounting.tipsStatus
											: null}
									</span>
								</div>
								<div className="col-md-12 mt-4 btn-blocks text-right">
									<button
										type="submit"
										disabled={!isDataChanged}
										className="btn btn-primary glow-primary mr-4"
									>
										Save Changes
									</button>
									<Link
										className="btn btn-outline-primary"
										to={`${URL.restaurants}/${slug}`}
									>
										Cancel
									</Link>
								</div>
							</div>
						</div>
					</div>
				</div>
			</form>
		</>
	);
};

export default AccountingSettings;
